import React, { useState, useCallback, useRef } from 'react';
import { Pill } from '../ds';
import './DayPartsGrid.css';

const DAYS = [
  { key: 'mon', label: '週一' },
  { key: 'tue', label: '週二' },
  { key: 'wed', label: '週三' },
  { key: 'thu', label: '週四' },
  { key: 'fri', label: '週五' },
  { key: 'sat', label: '週六' },
  { key: 'sun', label: '週日' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

// Generate all 48 half-hour slot strings: "0000", "0030", "0100", ...
const ALL_SLOTS = [];
for (let h = 0; h < 24; h++) {
  const hh = String(h).padStart(2, '0');
  ALL_SLOTS.push(`${hh}00`);
  ALL_SLOTS.push(`${hh}30`);
}

const EMPTY_VALUE = { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] };

export default function DayPartsGrid({ value = EMPTY_VALUE, onChange, disabled = false, isActive = false, onActiveChange }) {
  const gridRef = useRef(null);
  const selectionBoxRef = useRef(null);
  const dragStartRef = useRef(null); // { dayIndex, slotIndex, startX, startY }

  const isSelected = useCallback((dayKey, slot) => {
    return value[dayKey]?.includes(slot) || false;
  }, [value]);

  const toggleEntireDay = useCallback((dayKey) => {
    if (disabled) return;
    const current = value[dayKey] || [];
    const allSelected = current.length === 48;
    onChange({ ...value, [dayKey]: allSelected ? [] : [...ALL_SLOTS] });
  }, [value, onChange, disabled]);

  const selectAll = useCallback(() => {
    if (disabled) return;
    const all = {};
    for (const d of DAYS) all[d.key] = [...ALL_SLOTS];
    onChange(all);
  }, [onChange, disabled]);

  const clearAll = useCallback(() => {
    if (disabled) return;
    onChange({ ...EMPTY_VALUE });
  }, [onChange, disabled]);

  // Get cell position (dayIndex, slotIndex) from a DOM element
  const getCellPosition = useCallback((cellEl) => {
    const dayIndex = parseInt(cellEl.dataset.day, 10);
    const slotIndex = parseInt(cellEl.dataset.slot, 10);
    return { dayIndex, slotIndex };
  }, []);

  // Get cell element at a mouse position
  const getCellAtPoint = useCallback((x, y) => {
    const el = document.elementFromPoint(x, y);
    if (el && el.classList.contains('dayparts-cell')) return el;
    return null;
  }, []);

  // Rectangle selection: mousedown
  const handleMouseDown = useCallback((e) => {
    if (disabled) return;
    const cell = e.target;
    if (!cell.classList.contains('dayparts-cell')) return;
    e.preventDefault();

    const pos = getCellPosition(cell);
    dragStartRef.current = {
      ...pos,
      startX: e.clientX,
      startY: e.clientY,
      endDayIndex: pos.dayIndex,
      endSlotIndex: pos.slotIndex,
    };

    // Show selection box
    if (selectionBoxRef.current) {
      const rect = cell.getBoundingClientRect();
      const gridRect = gridRef.current.getBoundingClientRect();
      selectionBoxRef.current.style.display = 'block';
      selectionBoxRef.current.style.left = `${rect.left - gridRect.left}px`;
      selectionBoxRef.current.style.top = `${rect.top - gridRect.top}px`;
      selectionBoxRef.current.style.width = `${rect.width}px`;
      selectionBoxRef.current.style.height = `${rect.height}px`;
    }
  }, [disabled, getCellPosition]);

  // Rectangle selection: mousemove
  const handleMouseMove = useCallback((e) => {
    if (!dragStartRef.current || disabled) return;

    const cell = getCellAtPoint(e.clientX, e.clientY);
    if (!cell) return;

    const endPos = getCellPosition(cell);
    dragStartRef.current.endDayIndex = endPos.dayIndex;
    dragStartRef.current.endSlotIndex = endPos.slotIndex;

    // Update selection box overlay
    if (selectionBoxRef.current && gridRef.current) {
      const { dayIndex: startDI, slotIndex: startSI } = dragStartRef.current;
      const dayMin = Math.min(startDI, endPos.dayIndex);
      const dayMax = Math.max(startDI, endPos.dayIndex);
      const slotMin = Math.min(startSI, endPos.slotIndex);
      const slotMax = Math.max(startSI, endPos.slotIndex);

      // Find bounding cells
      const gridRect = gridRef.current.getBoundingClientRect();
      const topLeftCell = gridRef.current.querySelector(`[data-day="${dayMin}"][data-slot="${slotMin}"]`);
      const bottomRightCell = gridRef.current.querySelector(`[data-day="${dayMax}"][data-slot="${slotMax}"]`);

      if (topLeftCell && bottomRightCell) {
        const tlRect = topLeftCell.getBoundingClientRect();
        const brRect = bottomRightCell.getBoundingClientRect();
        selectionBoxRef.current.style.left = `${tlRect.left - gridRect.left}px`;
        selectionBoxRef.current.style.top = `${tlRect.top - gridRect.top}px`;
        selectionBoxRef.current.style.width = `${brRect.right - tlRect.left}px`;
        selectionBoxRef.current.style.height = `${brRect.bottom - tlRect.top}px`;
      }
    }
  }, [disabled, getCellAtPoint, getCellPosition]);

  // Rectangle selection: mouseup — invert selection in range
  const handleMouseUp = useCallback(() => {
    if (!dragStartRef.current) return;

    const { dayIndex: startDI, slotIndex: startSI, endDayIndex, endSlotIndex } = dragStartRef.current;
    const dayStart = Math.min(startDI, endDayIndex);
    const dayEnd = Math.max(startDI, endDayIndex);
    const slotStart = Math.min(startSI, endSlotIndex);
    const slotEnd = Math.max(startSI, endSlotIndex);

    const newValue = { ...value };
    for (let di = dayStart; di <= dayEnd; di++) {
      const dayKey = DAYS[di].key;
      const current = new Set(newValue[dayKey] || []);
      for (let si = slotStart; si <= slotEnd; si++) {
        const slot = ALL_SLOTS[si];
        if (current.has(slot)) current.delete(slot);
        else current.add(slot);
      }
      newValue[dayKey] = [...current].sort();
    }
    onChange(newValue);

    // Hide selection box
    if (selectionBoxRef.current) {
      selectionBoxRef.current.style.display = 'none';
    }
    dragStartRef.current = null;
  }, [value, onChange]);

  return (
    <div className="dayparts-wrapper">
      <div className="dayparts-actions">
        <Pill onClick={selectAll} disabled={disabled}>全選</Pill>
        <Pill onClick={clearAll} disabled={disabled}>清除</Pill>
      </div>
      <div
        className={`dayparts-grid ${disabled ? 'disabled' : ''}`}
        ref={gridRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div className="dayparts-selection-box" ref={selectionBoxRef} style={{ display: 'none' }} />

        {/* Header row */}
        <div className="dayparts-header-row">
          <div className="dayparts-day-label-cell"></div>
          {HOURS.map((h) => (
            <div key={h} className="dayparts-hour-header">
              {String(h).padStart(2, '0')}
            </div>
          ))}
        </div>

        {/* Day rows */}
        {DAYS.map((day, dayIndex) => (
          <div key={day.key} className="dayparts-day-row">
            <div className="dayparts-day-label-cell clickable" onClick={() => toggleEntireDay(day.key)}>
              {day.label}
            </div>
            {ALL_SLOTS.map((slot, slotIndex) => (
              <div
                key={slot}
                className={`dayparts-cell ${isSelected(day.key, slot) ? 'selected' : ''}`}
                data-day={dayIndex}
                data-slot={slotIndex}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="dayparts-legend">
        <span className="dayparts-legend-item"><span className="dayparts-legend-box selected"></span>已選取</span>
        <span className="dayparts-legend-item"><span className="dayparts-legend-box"></span>未選取</span>
        <span className="dayparts-legend-hint">拖拉框選反向切換時段（每格 30 分鐘）</span>
      </div>
    </div>
  );
}
